import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { InfoCard } from "@/components/LegalPage";
import {
  Users, Ban, Video, MessageSquare, Flag, ShieldAlert, Info,
  HeartHandshake, MessageCircle, Gavel,
} from "lucide-react";

export default function Community() {
  const sections = [
    {
      id: "the-vibe", title: "The Vibe", icon: HeartHandshake,
      body: (
        <>
          <p>
            MystiCast is a room. A loud, cheerful, sometimes weird room where creators host and viewers play alongside them.
            These guidelines exist so that room stays fun for everyone.
          </p>
          <p><strong>Short version:</strong> Build communities. Respect creators. Play fair.</p>
        </>
      ),
    },
    {
      id: "always-off-limits", title: "Always Off-Limits", icon: Ban, highlight: "pink",
      body: (
        <>
          <p>These are zero-tolerance. Immediate ban, and where applicable, referral to authorities:</p>
          <ul>
            <li><strong>Sexual, sexually explicit, or pornographic content of any kind.</strong> MystiCast is not an adult platform — no nudity, no sex acts, no sexualized posing, no &quot;lewd&quot; streams. Take it to a platform built for that.</li>
            <li><strong>Sexual content involving minors (CSAM).</strong> Immediate ban and referral to authorities.</li>
            <li><strong>Non-consensual intimate content.</strong></li>
            <li><strong>Violent extremism & incitement.</strong> Praising, promoting, or coordinating real-world violence.</li>
            <li><strong>Hate speech & targeted harassment.</strong> Slurs, dehumanization, brigading, doxxing.</li>
            <li><strong>Illegal transactions.</strong> Drugs, weapons, stolen accounts, hacked data.</li>
            <li><strong>Real-money gambling.</strong> Sparks and Gold Coins are not gambling instruments.</li>
            <li><strong>Impersonation & fraud.</strong></li>
            <li><strong>Malware & scams.</strong></li>
          </ul>
        </>
      ),
    },
    {
      id: "streaming-rules", title: "Streaming Rules", icon: Video, highlight: "cyan",
      body: (
        <>
          <ul>
            <li><strong>No sexual, sexually explicit, or pornographic content on stream. Period.</strong> No nudity, no sex acts, no sexualized dress-code or posing. We are 18+ for maturity, not for adult content.</li>
            <li>No minors on camera under any circumstances.</li>
            <li>No copyrighted music you don&apos;t have rights to. See our <Link to="/dmca">DMCA policy</Link>.</li>
            <li>Category/title should honestly reflect what&apos;s happening on stream. No hate-baiting or misleading thumbnails.</li>
            <li>Respect your guests — anyone brought on stage should consent to being on camera.</li>
          </ul>
          <InfoCard icon={Info} title="ℹ Music Reminder" tone="cyan">
            <p>Music from Spotify, Apple Music, YouTube Music, etc. is generally <strong>not</strong> licensed for public broadcast. Use royalty-free tracks or content you own.</p>
          </InfoCard>
        </>
      ),
    },
    {
      id: "chat-behavior", title: "Chat & Community Behavior", icon: MessageSquare,
      body: (
        <>
          <ul>
            <li>Be a person. Disagree strong, personal attacks weak.</li>
            <li>No spam, no repeated links, no pump-and-dump crypto/NFT nonsense.</li>
            <li>Respect moderators. If you&apos;re timed out or muted, take the note.</li>
            <li>Don&apos;t harass a creator&apos;s community into another creator&apos;s channel (raiding is a bannable offense).</li>
          </ul>
        </>
      ),
    },
    {
      id: "reporting", title: "Reporting", icon: Flag,
      body: (
        <p>
          Every stream, post, comment, DM, and profile has a Report button. <strong>Use it.</strong> Reports are reviewed by our Trust & Safety team.
          False reporting to harass a user is itself a violation.
        </p>
      ),
    },
    {
      id: "enforcement", title: "Enforcement Ladder", icon: ShieldAlert, highlight: "orange",
      body: (
        <>
          <InfoCard icon={Info} title="⚠ First-time minor infraction">
            <p>Warning + content removal.</p>
          </InfoCard>
          <InfoCard icon={ShieldAlert} title="⏱ Repeat or moderate infraction" tone="orange">
            <p>Temporary suspension (24 hours &ndash; 30 days) depending on severity and history.</p>
          </InfoCard>
          <InfoCard icon={Ban} title="🚫 Severe or repeated violations" tone="pink">
            <p>Permanent ban, and where applicable, referral to law enforcement.</p>
          </InfoCard>
          <p className="mt-3">Enforcement is applied per-case and considers context. We prefer education over punishment for anything short of the &quot;always off-limits&quot; list.</p>
        </>
      ),
    },
    {
      id: "appeals", title: "Appeals", icon: Gavel,
      body: (
        <p>
          Every enforcement action can be appealed via the <Link to="/contact">contact page</Link>. Include your username and the action you&apos;re appealing.
          We review appeals within <strong>5 business days</strong>.
        </p>
      ),
    },
    {
      id: "contact", title: "Contact", icon: MessageCircle,
      body: (<p>Questions or concerns? Reach us via the <Link to="/contact">contact page</Link>.</p>),
    },
  ];

  return (
    <LegalPage
      icon={Users}
      label="COMMUNITY GUIDELINES"
      title="Community Guidelines"
      intro="Build communities. Respect creators. Play fair."
      updated="February 2026"
      sections={sections}
      contact={{
        title: "Report or Appeal",
        body: "Trust & Safety is a real team, not a bot. We read every report and every appeal.",
        cta: { label: "Contact Trust & Safety", to: "/contact" },
      }}
    />
  );
}
